import React, { useState } from 'react';
import { SectorOpportunityTypeChips, SectorLensPanel } from './SectorLens';
import { labelsForMode, MODES } from '../lib/opportunityMode';
import {
  ANALYSIS_TABS,
  DEFAULT_ANALYSIS_TAB,
  hasMarketplaceFitTab,
  getMarketplaceFitContent,
  readinessPillLabel,
  formatMarketplaceRecommendation,
} from '../lib/analysisTabs';

const TOOLTIPS = {
  engagement_fit: 'The primary engagement shape this target appears best suited to, based on all available signal evidence.',
  suitable_engagements: 'Ranked list of engagement types that appear viable for this target, based on risk, capacity, readiness, and signal evidence.',
  commercial_capacity: 'Inferred ability to support engagement spend, based on funding, M&A, hiring, company scale, and transformation signals.',
  buying_readiness: 'How likely the organisation appears to be in a position to buy external support, based on transformation activity, intervention likelihood, and vendor patterns.',
  affordability: 'The likely size of engagement that appears realistic given commercial capacity and evidence strength.',
};

function LevelBadge({ level }) {
  const cls = {
    'Strategic': 'badge-high', 'Strong': 'badge-high', 'Likely Funded': 'badge-high',
    'Active': 'badge-medium', 'Moderate': 'badge-medium', 'Emerging': 'badge-medium',
    'Low': 'badge-low', 'Minimal': 'badge-low',
  }[level] || 'badge-type';
  return <span className={`badge ${cls}`} style={{ fontSize: '12px', fontWeight: '600' }}>{level}</span>;
}

function FitBadge({ level }) {
  const cls = level === 'High' ? 'badge-high' : level === 'Medium' ? 'badge-medium' : 'badge-low';
  return <span className={`badge ${cls}`} style={{ fontSize: '11px', fontWeight: '600' }}>{level} fit</span>;
}

function LevelBar({ score }) {
  const color = score >= 70 ? 'var(--success)' : score >= 45 ? 'var(--warning)' : score >= 20 ? 'var(--primary)' : 'var(--text-secondary)';
  return (
    <div style={{ height: '5px', borderRadius: '3px', background: 'var(--border)', width: '100%', marginTop: '4px' }}>
      <div style={{ height: '100%', borderRadius: '3px', background: color, width: `${Math.min(100, score)}%`, transition: 'width 0.3s' }} />
    </div>
  );
}

function Tip({ text, children }) {
  return (
    <span style={{ cursor: 'help', borderBottom: '1px dotted var(--text-secondary)' }} title={text}>
      {children}
    </span>
  );
}

export default function FitResults({ fit, onBack, analysisId, onDownloadPDF, onDownloadPNG }) {
  // Iter 35d — Two-tab UI. Consultancy View is the protected default
  // and always renders the legacy Engagement Fit layout. Marketplace
  // Opportunity tab appears only when the Iter-35a additive
  // `marketplace_opportunity.fit_pack` is present and has usable
  // content.
  const [activeTab, setActiveTab] = useState(DEFAULT_ANALYSIS_TAB);
  const showMarketplaceTab = hasMarketplaceFitTab(fit);
  const resolvedTab = (activeTab === ANALYSIS_TABS.MARKETPLACE && showMarketplaceTab)
    ? ANALYSIS_TABS.MARKETPLACE
    : ANALYSIS_TABS.CONSULTANCY;

  if (!fit) return null;

  return (
    <div>
      {showMarketplaceTab && (
        <FitTabBar
          activeTab={resolvedTab}
          onChange={setActiveTab}
        />
      )}
      {resolvedTab === ANALYSIS_TABS.CONSULTANCY ? (
        <ConsultancyFitView
          fit={fit}
          onBack={onBack}
          onDownloadPDF={onDownloadPDF}
          onDownloadPNG={onDownloadPNG}
        />
      ) : (
        <MarketplaceFitView
          fit={fit}
          onBack={onBack}
          onDownloadPDF={onDownloadPDF}
          onDownloadPNG={onDownloadPNG}
        />
      )}
    </div>
  );
}


// ── Tab bar ──────────────────────────────────────────────────────────

function FitTabBar({ activeTab, onChange }) {
  return (
    <div
      className="panel"
      style={{ marginBottom: '8px', padding: 0 }}
      data-testid="fit-tabs"
    >
      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <TabButton
          label="Consultancy View"
          active={activeTab === ANALYSIS_TABS.CONSULTANCY}
          onClick={() => onChange(ANALYSIS_TABS.CONSULTANCY)}
          testId="fit-tab-consultancy"
        />
        <TabButton
          label="Marketplace Opportunity"
          active={activeTab === ANALYSIS_TABS.MARKETPLACE}
          onClick={() => onChange(ANALYSIS_TABS.MARKETPLACE)}
          testId="fit-tab-marketplace"
        />
      </div>
    </div>
  );
}

function TabButton({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-selected={active}
      role="tab"
      style={{
        appearance: 'none',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
        color: active ? 'var(--primary)' : 'var(--text-secondary)',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: active ? 600 : 500,
        letterSpacing: '0.02em',
        padding: '10px 16px',
        marginBottom: '-1px',
      }}
    >
      {label}
    </button>
  );
}


// ── Consultancy View (legacy Engagement Fit, restored as default) ──

function ConsultancyFitView({ fit, onBack, onDownloadPDF, onDownloadPNG }) {
  const { engagement_fit, suitable_engagements, commercial_capacity, buying_readiness, affordability,
    commercial_notes, evidence_summary, signal_profile, verification_status, sector_lens } = fit;

  // Iter 35d — Consultancy View *always* uses consultancy labels and
  // the legacy fit layout regardless of the persisted
  // `opportunity_mode`. The Iter 34 mode-aware hero / score / pill
  // override is no longer the default — it moves to the Marketplace
  // tab.
  const labels = labelsForMode(MODES.CONSULTANCY);
  const sectorOpportunityTypes = sector_lens?.opportunity_types || [];

  return (
    <div data-testid="fit-consultancy-view">
      <div className="panel" style={{ marginBottom: '12px' }}>
        <div className="panel-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }} data-testid="fit-title">
              {labels.fitTitle}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {fit.scope_label || fit.company_name}
              {verification_status && (
                <span className={`badge ${verification_status === 'verified' ? 'badge-high' : verification_status === 'corroborated' ? 'badge-medium' : 'badge-low'}`}
                  style={{ marginLeft: '8px', fontSize: '10px' }} data-testid="fit-verification-status">
                  {verification_status.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={onBack} data-testid="fit-back-button">
            Back to Fix
          </button>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: '12px', borderLeft: '4px solid var(--primary)' }}>
        <div className="panel-body" style={{ padding: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '6px' }}>
            <Tip text={TOOLTIPS.engagement_fit}>{labels.fitHero}</Tip>
          </div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text)' }} data-testid="fit-engagement-type">
            {engagement_fit?.type || 'Assessment pending'}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '6px', lineHeight: '1.5' }} data-testid="fit-engagement-rationale">
            {engagement_fit?.rationale}
          </div>
        </div>
      </div>

      {sectorOpportunityTypes.length > 0 && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="fit-sector-opportunity-types">
          <div className="panel-header">
            {sector_lens?.sector
              ? `${sector_lens.sector} — opportunity types`
              : 'Sector opportunity types'}
          </div>
          <div className="panel-body" style={{ padding: '10px 12px' }}>
            <SectorOpportunityTypeChips
              opportunityTypes={sectorOpportunityTypes}
              max={12}
              testIdPrefix="fit-sector-opportunity-types-chips"
            />
          </div>
        </div>
      )}

      {sector_lens && (sector_lens.lens?.fit_hint || (sector_lens.categories || []).length > 0) && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="fit-sector-why">
          <div className="panel-header">Why this fits this sector</div>
          <div className="panel-body" style={{ padding: '0' }}>
            <SectorLensPanel
              sectorLens={{ ...sector_lens, opportunity_types: [] }}
              testIdPrefix="fit-sector-why-panel"
            />
          </div>
        </div>
      )}

      {suitable_engagements && suitable_engagements.length > 0 && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="fit-suitable-engagements-section">
          <div className="panel-header">
            <Tip text={TOOLTIPS.suitable_engagements}>Suitable Engagement Types</Tip>
          </div>
          <div className="panel-body" style={{ padding: '4px 8px' }}>
            {suitable_engagements.map((eng, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '10px 12px',
                borderBottom: i < suitable_engagements.length - 1 ? '1px solid var(--border)' : 'none',
              }} data-testid={`suitable-engagement-${i}`}>
                <div style={{ minWidth: '80px', paddingTop: '2px' }}>
                  <FitBadge level={eng.fit} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>
                    {eng.type}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5', marginTop: '2px' }}>
                    {eng.reason}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px', marginBottom: '12px' }}>
        <div className="panel" data-testid="fit-commercial-capacity">
          <div className="panel-body" style={{ padding: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '8px' }}>
              <Tip text={TOOLTIPS.commercial_capacity}>Commercial Capacity</Tip>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <LevelBadge level={commercial_capacity?.level} />
            </div>
            <LevelBar score={commercial_capacity?.score || 0} />
            {commercial_capacity?.factors?.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {commercial_capacity.factors.slice(0, 3).map((f, i) => (
                  <div key={i} style={{ fontSize: '11px', color: 'var(--text-secondary)', padding: '1px 0' }}>&bull; {f}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="panel" data-testid="fit-buying-readiness">
          <div className="panel-body" style={{ padding: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '8px' }}>
              <Tip text={TOOLTIPS.buying_readiness}>Buying Readiness</Tip>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <LevelBadge level={buying_readiness?.level} />
            </div>
            <LevelBar score={buying_readiness?.score || 0} />
            {buying_readiness?.factors?.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {buying_readiness.factors.slice(0, 3).map((f, i) => (
                  <div key={i} style={{ fontSize: '11px', color: 'var(--text-secondary)', padding: '1px 0' }}>&bull; {f}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="panel" data-testid="fit-affordability">
          <div className="panel-body" style={{ padding: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '8px' }}>
              <Tip text={TOOLTIPS.affordability}>Affordability / Budget Fit</Tip>
            </div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)', lineHeight: '1.5' }} data-testid="fit-affordability-label">
              {affordability?.label || 'Evidence limited'}
            </div>
          </div>
        </div>
      </div>

      {commercial_notes && (
        <div className="panel" style={{ marginBottom: '12px' }}>
          <div className="panel-header">Commercial Notes</div>
          <div className="panel-body">
            <div className="executive-summary" data-testid="fit-commercial-notes" style={{ whiteSpace: 'pre-line' }}>
              {commercial_notes}
            </div>
          </div>
        </div>
      )}

      {evidence_summary && evidence_summary.length > 0 && (
        <div className="panel" style={{ marginBottom: '12px' }}>
          <div className="panel-header">Supporting Evidence</div>
          <div className="panel-body" style={{ padding: '8px' }}>
            {evidence_summary.map((ev, i) => (
              <div key={i} style={{ marginBottom: '10px', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)' }} data-testid={`fit-evidence-${i}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600' }}>{ev.category}</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{ev.impact}</span>
                </div>
                {ev.signals && ev.signals.map((s, j) => (
                  <div key={j} style={{ fontSize: '11px', color: 'var(--text-secondary)', padding: '1px 0' }}>&bull; {s}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {signal_profile && (
        <div className="panel" style={{ marginBottom: '12px' }}>
          <div className="panel-body" style={{ padding: '8px 16px', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '11px', color: 'var(--text-secondary)' }} data-testid="fit-signal-profile">
            <span>{signal_profile.total_signals} signals analysed</span>
            <span>{signal_profile.funding_signals} funding</span>
            <span>{signal_profile.growth_signals} growth</span>
            <span>{signal_profile.stress_signals} stress</span>
            {signal_profile.has_ch_data && <span>Companies House data available</span>}
          </div>
        </div>
      )}

      <FitActionBar
        onDownloadPDF={onDownloadPDF}
        onDownloadPNG={onDownloadPNG}
        onBack={onBack}
      />
    </div>
  );
}


// ── Marketplace Opportunity View (sourced from marketplace_opportunity.fit_pack) ──

function MarketplaceFitView({ fit, onBack, onDownloadPDF, onDownloadPNG }) {
  const content = getMarketplaceFitContent(fit);
  if (!content) return null;

  const {
    verdict, score, angle, fit_drivers, first_action,
    evidence_strength, roles, watchouts, contactability,
    urgency, procurement_urgency, buy_vs_hire, readiness_mode,
    sector,
    best_fit_role, best_fit_role_label, role_scores,
    role_explanation, role_watchouts, evidence_to_validate,
  } = content;

  const pill = readinessPillLabel(readiness_mode);
  // Iter 36f-2 — verdict on Sport is a full sentence ("Priority —
  // engage now"); on Construction it remains a token band.
  // formatMarketplaceRecommendation upper-cases & strips
  // underscores; that's appropriate for both.
  const verdictLabel = formatMarketplaceRecommendation(verdict);
  // Verdict tone derived from the leading band token so Sport's
  // sentence form still maps to a colour.
  const verdictClass = (() => {
    if (!verdict) return 'badge-type';
    const v = String(verdict).toLowerCase();
    if (v.startsWith('priority') || v === 'engage_now' || v === 'quote_now') return 'badge-high';
    if (v.startsWith('engage')) return 'badge-high';
    if (v.startsWith('warm') || v === 'warm_verify' || v === 'pre_qualify') return 'badge-medium';
    if (v.startsWith('verify')) return 'badge-medium';
    if (v.startsWith('watch')) return 'badge-low';
    return 'badge-type';
  })();

  return (
    <div data-testid="fit-marketplace-view">
      <div className="panel" style={{ marginBottom: '12px' }}>
        <div className="panel-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div
              style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
              data-testid="marketplace-fit-title"
            >
              Marketplace Opportunity Fit
              {best_fit_role_label && (
                <span className="badge badge-type" style={{ fontSize: '10px' }} data-testid="marketplace-fit-role-chip">
                  Role: {best_fit_role_label}
                </span>
              )}
              {pill && (
                <span className="badge badge-type" style={{ fontSize: '10px' }} data-testid="marketplace-fit-mode-pill">
                  {pill}
                </span>
              )}
              {sector && (
                <span className="badge badge-type" style={{ fontSize: '10px' }} data-testid="marketplace-fit-sector-badge">
                  {sector}
                </span>
              )}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {fit.scope_label || fit.company_name}
            </div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={onBack} data-testid="marketplace-fit-back-button">
            Back to Fix
          </button>
        </div>
      </div>

      {/* Marketplace Opportunity Verdict hero. */}
      <div className="panel" style={{ marginBottom: '12px', borderLeft: '4px solid var(--primary)' }} data-testid="marketplace-fit-verdict">
        <div className="panel-body" style={{ padding: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: '6px' }}>
            Marketplace Opportunity Verdict
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {verdictLabel ? (
              <span className={`badge ${verdictClass}`} style={{ fontSize: '13px', fontWeight: '700', padding: '6px 10px' }} data-testid="marketplace-fit-verdict-label">
                {verdictLabel}
              </span>
            ) : (
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                Verdict pending — re-run Fit to populate.
              </span>
            )}
            {typeof score === 'number' && (
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }} data-testid="marketplace-fit-score">
                <strong>Score:</strong> {score} / 100
              </span>
            )}
          </div>
          {typeof score === 'number' && (
            <div style={{ marginTop: '10px', maxWidth: '320px' }}>
              <LevelBar score={score} />
            </div>
          )}
        </div>
      </div>

      {(best_fit_role_label || (role_scores && Object.keys(role_scores).length > 0)
        || (role_explanation && role_explanation.length > 0)) && (
        <div className="panel" style={{ marginBottom: '12px' }} data-testid="marketplace-fit-role-panel">
          <div className="panel-header" title="Role classification derived from the single Marketplace role authority">
            Marketplace Role
          </div>
          <div className="panel-body" style={{ padding: '10px 14px' }}>
            {best_fit_role_label && (
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }} data-testid="marketplace-fit-role-name">
                {best_fit_role_label}
                {best_fit_role && (
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginLeft: '6px' }}>
                    ({best_fit_role})
                  </span>
                )}
              </div>
            )}
            {role_scores && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', marginBottom: '6px' }} data-testid="marketplace-fit-role-scores">
                {typeof role_scores.buyer_score === 'number' && (
                  <span data-testid="marketplace-fit-role-score-buyer"><strong>Buyer:</strong> {role_scores.buyer_score}</span>
                )}
                {typeof role_scores.supplier_partner_score === 'number' && (
                  <span data-testid="marketplace-fit-role-score-supplier-partner"><strong>Supplier / Partner:</strong> {role_scores.supplier_partner_score}</span>
                )}
                {typeof role_scores.ecosystem_score === 'number' && (
                  <span data-testid="marketplace-fit-role-score-ecosystem"><strong>Ecosystem:</strong> {role_scores.ecosystem_score}</span>
                )}
              </div>
            )}
            {role_explanation && role_explanation.length > 0 && (
              <div style={{ marginTop: '6px' }} data-testid="marketplace-fit-role-explanation">
                {role_explanation.map((e, i) => (
                  <div key={i} style={{ fontSize: '12px', color: 'var(--text)', padding: '1px 0' }} data-testid={`marketplace-fit-role-explanation-${i}`}>
                    &bull; {e}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="analysis-grid" style={{ marginBottom: '12px' }}>
        <div>
          {roles && roles.length > 0 ? (
            <div className="panel" data-testid="marketplace-fit-roles-panel">
              <div className="panel-header" title="Marketplace participant roles registered for this sector">
                Sector Marketplace Roles
              </div>
              <div className="panel-body" style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {roles.map((role, i) => (
                  <span
                    key={`${role}-${i}`}
                    className="badge badge-type"
                    style={{ fontSize: '11px', textTransform: 'capitalize' }}
                    data-testid={`marketplace-fit-role-${i}`}
                  >
                    {role}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <Section
              title="Sector Marketplace Roles"
              testId="marketplace-fit-roles-empty"
              empty="No marketplace roles registered for this sector."
            />
          )}
          <Section
            title="Commercial Value to Marketplace"
            tooltip="The product / supply / partnership angle this target appears best matched to"
            testId="marketplace-fit-angle"
            empty="No commercial angle surfaced yet — verify approved evidence."
            spacingTop
          >
            {angle}
          </Section>
          <Section
            title="Recommended Next Action"
            tooltip="The single most actionable next move for the operator"
            testId="marketplace-fit-next-action"
            empty="No first action recommended — re-run Fit once approved signals are richer."
            spacingTop
          >
            {first_action}
          </Section>
          {evidence_to_validate && evidence_to_validate.length > 0 && (
            <div className="panel" style={{ marginTop: '4px' }} data-testid="marketplace-fit-evidence-to-validate">
              <div className="panel-header" title="Evidence the operator must validate before committing to this fit verdict">
                Evidence to Validate
              </div>
              <div className="panel-body" style={{ padding: '10px 14px' }}>
                {evidence_to_validate.map((e, i) => (
                  <div
                    key={i}
                    style={{ fontSize: '12px', color: 'var(--text)', padding: '2px 0' }}
                    data-testid={`marketplace-fit-evidence-to-validate-${i}`}
                  >
                    &bull; {e}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <FitDriversPanel drivers={fit_drivers} />
          <ContactabilityPanel contactability={contactability} />
          {readiness_mode === 'purchase_readiness' && typeof urgency === 'number' && (
            <Section
              title="Urgency"
              tooltip="Time-sensitivity of the marketplace opportunity (Sport readiness)"
              testId="marketplace-fit-urgency"
              spacingTop
            >
              <span style={{ fontWeight: 600 }}>{urgency} / 100</span>
              <LevelBar score={urgency} />
            </Section>
          )}
          {readiness_mode === 'supply_readiness' && procurement_urgency && (
            <Section
              title="Procurement Urgency"
              tooltip="Procurement window and urgency scores (Construction readiness)"
              testId="marketplace-fit-procurement-urgency"
              spacingTop
            >
              {typeof procurement_urgency.window_score === 'number' && (
                <div style={{ marginBottom: '6px' }}>
                  <strong>Window:</strong> {procurement_urgency.window_score} / 100
                  <LevelBar score={procurement_urgency.window_score} />
                </div>
              )}
              {typeof procurement_urgency.urgency_score === 'number' && (
                <div>
                  <strong>Urgency:</strong> {procurement_urgency.urgency_score} / 100
                  <LevelBar score={procurement_urgency.urgency_score} />
                </div>
              )}
            </Section>
          )}
          {readiness_mode === 'supply_readiness' && typeof buy_vs_hire === 'number' && (
            <Section
              title="Buy vs Hire"
              tooltip="How well the procurement signal matches buy-vs-hire framework patterns (Construction readiness)"
              testId="marketplace-fit-buy-vs-hire"
              spacingTop
            >
              <span style={{ fontWeight: 600 }}>{buy_vs_hire} / 100</span>
              <LevelBar score={buy_vs_hire} />
            </Section>
          )}
        </div>
      </div>

      {evidence_strength && (
        <Section
          title="Evidence Strength"
          tooltip="Approved-signal corpus strength behind this marketplace fit"
          testId="marketplace-fit-evidence-strength"
        >
          {evidence_strength}
        </Section>
      )}

      {((watchouts && watchouts.length > 0)
        || (role_watchouts && role_watchouts.length > 0)) && (
        <div className="panel" style={{ marginTop: '4px', marginBottom: '12px' }} data-testid="marketplace-fit-watchouts">
          <div className="panel-header" title="Reasons to verify or hold before onboarding / contacting the target">
            Risks / Watch-outs
          </div>
          <div className="panel-body" style={{ padding: '8px 14px' }}>
            {(watchouts || []).map((w, i) => (
              <div
                key={`w-${i}`}
                style={{ fontSize: '12px', padding: '3px 0', color: 'var(--warning)' }}
                data-testid={`marketplace-fit-watchout-${i}`}
              >
                &bull; {w}
              </div>
            ))}
            {(role_watchouts || []).map((w, i) => (
              <div
                key={`rw-${i}`}
                style={{ fontSize: '12px', padding: '3px 0', color: 'var(--warning)' }}
                data-testid={`marketplace-fit-role-watchout-${i}`}
              >
                &bull; {w}
              </div>
            ))}
          </div>
        </div>
      )}

      <FitActionBar
        onDownloadPDF={onDownloadPDF}
        onDownloadPNG={onDownloadPNG}
        onBack={onBack}
      />
    </div>
  );
}


// ── Marketplace tab sub-panels ──────────────────────────────────────

function FitDriversPanel({ drivers }) {
  if (!drivers || drivers.length === 0) {
    return (
      <Section
        title="Fit Drivers"
        tooltip="The strongest readiness dimensions driving this marketplace fit"
        testId="marketplace-fit-drivers"
        empty="No standout fit drivers surfaced. Re-run Fit once approved signals are richer."
      />
    );
  }
  return (
    <div className="panel" data-testid="marketplace-fit-drivers">
      <div className="panel-header" title="The strongest readiness dimensions driving this marketplace fit">
        Fit Drivers
      </div>
      <div className="panel-body" style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {drivers.map((d, i) => (
          <span
            key={`${d}-${i}`}
            className="badge badge-type"
            style={{ fontSize: '11px' }}
            data-testid={`marketplace-fit-driver-${i}`}
          >
            {d}
          </span>
        ))}
      </div>
    </div>
  );
}

function ContactabilityPanel({ contactability }) {
  if (!contactability) return null;
  const score = typeof contactability.score === 'number' ? contactability.score : null;
  const url = contactability.contact_url || null;
  let host = url;
  if (url) {
    try { host = new URL(url).hostname.replace(/^www\./, ''); } catch { host = url; }
  }
  return (
    <div className="panel" style={{ marginTop: '4px' }} data-testid="marketplace-fit-contactability">
      <div className="panel-header" title="How well a usable contact route was discovered for this target">
        Contactability
      </div>
      <div className="panel-body" style={{ padding: '10px 14px', fontSize: '13px' }}>
        {score !== null && (
          <div style={{ marginBottom: url ? '8px' : 0 }}>
            <strong>Score:</strong> {score} / 100
            <LevelBar score={score} />
          </div>
        )}
        {url && (
          <div data-testid="marketplace-fit-contact-url">
            <strong>Contact:</strong>{' '}
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>
              {host}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Shared bits ──────────────────────────────────────────────────────

function Section({ title, tooltip, testId, children, empty, spacingTop }) {
  return (
    <div className="panel" style={{ marginTop: spacingTop ? '4px' : 0 }} data-testid={testId}>
      <div className="panel-header" title={tooltip}>{title}</div>
      <div className="panel-body" style={{ padding: '10px 14px', fontSize: '13px', lineHeight: '1.5' }}>
        {children ? children : (
          <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>{empty}</span>
        )}
      </div>
    </div>
  );
}

function FitActionBar({ onDownloadPDF, onDownloadPNG, onBack }) {
  return (
    <div style={{ paddingTop: '8px', display: 'flex', gap: '8px' }}>
      {onDownloadPDF && (
        <button className="btn btn-primary" onClick={onDownloadPDF} data-testid="download-fit-pdf-button">
          Download PDF
        </button>
      )}
      {onDownloadPNG && (
        <button className="btn btn-outline" onClick={onDownloadPNG} data-testid="download-fit-png-button">
          Export as PNG
        </button>
      )}
      <button className="btn btn-outline" onClick={onBack} data-testid="fit-back-to-fix">
        Back to Fix
      </button>
    </div>
  );
}
